import { useEffect, useMemo, useState } from 'react';
import { Image, ImageSourcePropType, Pressable, ScrollView, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import BottomTabBar from '../components/BottomTabBar';
import { ActionHeader } from '../components/AppHeader';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { getRecentlyViewed, RecentlyViewedItem } from '../utils/recentlyViewed';
import { isLoggedIn, logout } from '../api/auth';
import { getFavoritePeople, getLibrary } from '../api/library';
import { Audiobook, AudiobookPerson } from '../api/audiobooks';
import { ChildProfile, getActiveProfile, getCachedActiveProfile } from '../api/profiles';
import ChildProfileBadge from '../components/ChildProfileBadge';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'Library'>;

type QuickAction = {
  title: string;
  icon: keyof typeof Ionicons.glyphMap;
  color: string;
  onPress: () => void;
};

type SavedItem = {
  id: string;
  title: string;
  author: string;
  image: ImageSourcePropType;
  onPress: () => void;
};

type FavoritePersonItem = {
  id: number;
  name: string;
  image: ImageSourcePropType;
};

const fallbackCover = require('../../assets/aural-continue-thumb.png');

function imageSource(url?: string | null): ImageSourcePropType {
  return url ? { uri: url } : fallbackCover;
}

export default function LibraryScreen({ navigation }: Props) {
  const { width } = useWindowDimensions();
  const [recent, setRecent] = useState<RecentlyViewedItem | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [profile, setProfile] = useState<ChildProfile | null>(() => getCachedActiveProfile());
  const [favorites, setFavorites] = useState<Audiobook[]>([]);
  const [favoritePeople, setFavoritePeople] = useState<AudiobookPerson[]>([]);
  const cardWidth = (width - 48 - 16) / 2;

  useEffect(() => {
    const loadState = () => {
      const hasSession = isLoggedIn();
      setRecent(getRecentlyViewed());
      setLoggedIn(hasSession);

      if (!hasSession) {
        setProfile(null);
        setFavorites([]);
        setFavoritePeople([]);
        return;
      }

      const cached = getCachedActiveProfile();
      if (cached) setProfile(cached);

      getActiveProfile().then(setProfile).catch(() => setProfile(null));
      getLibrary('favorites').then(setFavorites).catch(() => setFavorites([]));
      getFavoritePeople().then(setFavoritePeople).catch(() => setFavoritePeople([]));
    };

    loadState();
    const unsubscribe = navigation.addListener('focus', loadState);
    return unsubscribe;
  }, [navigation]);

  const handleLogout = async () => {
    await logout();
    setLoggedIn(false);
    setProfile(null);
    setFavorites([]);
    setFavoritePeople([]);
    navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
  };

  const quickActions: QuickAction[] = [
    {
      title: 'Đang nghe',
      icon: 'play-circle',
      color: colors.blue,
      onPress: () => loggedIn ? navigation.navigate('Downloads', { title: 'Đang nghe', libraryType: 'listening' }) : recent && navigation.navigate('Player', { id: recent.id, slug: recent.slug }),
    },
    {
      title: 'Yêu thích',
      icon: 'heart',
      color: colors.accent,
      onPress: () => loggedIn ? navigation.navigate('Downloads', { title: 'Yêu thích', libraryType: 'favorites' }) : navigation.navigate('Login'),
    },
    {
      title: 'Lịch sử nghe',
      icon: 'time',
      color: colors.orange,
      onPress: () => loggedIn ? navigation.navigate('Downloads', { title: 'Lịch sử nghe', libraryType: 'viewed' }) : navigation.navigate('Login'),
    },
  ];

  const savedItems = useMemo<SavedItem[]>(() => {
    if (!loggedIn) return [];

    return favorites.map((book) => ({
      id: String(book.id),
      title: book.name,
      author: narratorName(book),
      image: imageSource(book.cover),
      onPress: () => navigation.navigate('BookOverview', { id: book.id, slug: book.slug }),
    }));
  }, [favorites, loggedIn, navigation]);

  const favoritePersonItems = useMemo<FavoritePersonItem[]>(() => {
    if (!loggedIn) return [];

    return favoritePeople.map((person) => ({
      id: person.id,
      name: person.name,
      image: imageSource(person.avatar),
    }));
  }, [favoritePeople, loggedIn]);

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <ActionHeader
          onBack={() => navigation.goBack()}
          rightIcons={['search-outline', 'ellipsis-horizontal']}
          onRightPress={[() => navigation.navigate('GetSuggestions')]}
        />

        <Text style={styles.title}>Thư viện</Text>
        <Text style={styles.subtitle}>Quản lý nội dung đang nghe, yêu thích và lịch sử nghe sách.</Text>

        {loggedIn ? (
          <View style={styles.profileSection}>
            <ChildProfileBadge profile={profile} subtitle="Thư viện cá nhân" onLogout={handleLogout} />
          </View>
        ) : (
          <View style={styles.loginRequired}>
            <Ionicons name="lock-closed" size={28} color={colors.button} />
            <Text style={styles.loginTitle}>Đăng nhập để quản lý thư viện</Text>
            <Text style={styles.loginText}>Sau khi đăng nhập, bé có thể lưu truyện yêu thích và xem lại lịch sử nghe.</Text>
            <Pressable style={styles.loginButton} onPress={() => navigation.navigate('Login')}>
              <Text style={styles.loginButtonText}>Đăng nhập</Text>
            </Pressable>
          </View>
        )}

        <View style={styles.quickRow}>
          {quickActions.map((action) => (
            <Pressable key={action.title} style={styles.quickCard} onPress={action.onPress}>
              <View style={[styles.quickIcon, { backgroundColor: action.color }]}>
                <Ionicons name={action.icon} size={21} color={colors.buttonText} />
              </View>
              <Text style={styles.quickTitle} numberOfLines={2}>{action.title}</Text>
            </Pressable>
          ))}
        </View>

        {recent ? (
          <Pressable style={styles.continueCard} onPress={() => navigation.navigate('Player', { id: recent.id, slug: recent.slug })}>
            <Image source={imageSource(recent.cover)} style={styles.continueCover} resizeMode="cover" />
            <View style={styles.continueText}>
              <Text style={styles.kicker}>Nghe tiếp</Text>
              <Text style={styles.continueTitle} numberOfLines={2}>{recent.title}</Text>
              <Text style={styles.continueMeta} numberOfLines={1}>{recent.chapterTitle || recent.author}</Text>
            </View>
            <View style={styles.playButton}>
              <Ionicons name="play" size={18} color={colors.buttonText} />
            </View>
          </Pressable>
        ) : null}

        <Text style={styles.sectionTitle}>Danh sách đã lưu</Text>
        {loggedIn && favoritePersonItems.length ? (
          <View style={styles.favoritePeopleSection}>
            <Text style={styles.subsectionTitle}>Người đọc sách yêu thích</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.peopleRow}>
              {favoritePersonItems.map((person) => (
                <Pressable key={person.id} style={styles.personCard} onPress={() => navigation.navigate('AuthorDetails', { id: person.id })}>
                  <Image source={person.image} style={styles.personAvatar} resizeMode="cover" />
                  <Text style={styles.personName} numberOfLines={1}>{person.name}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        ) : null}
        {loggedIn ? (
          savedItems.length ? (
            <View style={styles.savedGrid}>
              {savedItems.map((item) => (
                <Pressable key={item.id} style={[styles.savedCard, { width: cardWidth }]} onPress={item.onPress}>
                  <Image source={item.image} style={styles.savedCover} resizeMode="cover" />
                  <Text style={styles.savedTitle} numberOfLines={2}>{item.title}</Text>
                  <Text style={styles.savedAuthor} numberOfLines={1}>{item.author}</Text>
                </Pressable>
              ))}
            </View>
          ) : (
            <View style={styles.emptySaved}>
              <Text style={styles.emptyTitle}>Chưa có truyện đã lưu</Text>
              <Text style={styles.emptyText}>Các truyện yêu thích sẽ xuất hiện ở đây.</Text>
            </View>
          )
        ) : (
          <View style={styles.emptySaved}>
            <Text style={styles.emptyTitle}>Cần đăng nhập</Text>
            <Text style={styles.emptyText}>Đăng nhập để xem và quản lý danh sách đã lưu.</Text>
          </View>
        )}
      </ScrollView>

      <BottomTabBar
        active="My Library"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => null}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingTop: 62,
    paddingHorizontal: 24,
    paddingBottom: 124,
  },
  title: {
    color: colors.textPrimary,
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 8,
  },
  subtitle: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 18,
  },
  profileSection: {
    marginBottom: 18,
  },
  loginRequired: {
    alignItems: 'center',
    backgroundColor: colors.panel,
    borderRadius: 24,
    padding: 20,
    marginBottom: 18,
  },
  loginTitle: {
    color: colors.textPrimary,
    fontSize: 17,
    fontWeight: '900',
    marginTop: 10,
    marginBottom: 6,
  },
  loginText: {
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    marginBottom: 16,
  },
  loginButton: {
    height: 44,
    minWidth: 150,
    borderRadius: 18,
    backgroundColor: colors.button,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginButtonText: {
    color: colors.buttonText,
    fontWeight: '900',
    fontSize: 14,
  },
  quickRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  quickCard: {
    flex: 1,
    minHeight: 104,
    borderRadius: 22,
    backgroundColor: colors.panel,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  quickIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 9,
  },
  quickTitle: {
    color: colors.textPrimary,
    fontSize: 12,
    lineHeight: 15,
    fontWeight: '900',
    textAlign: 'center',
  },
  continueCard: {
    minHeight: 108,
    borderRadius: 24,
    backgroundColor: colors.blue,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 24,
  },
  continueCover: {
    width: 78,
    height: 78,
    borderRadius: 18,
    backgroundColor: colors.card,
    marginRight: 14,
  },
  continueText: {
    flex: 1,
  },
  kicker: {
    color: colors.yellow,
    fontSize: 12,
    fontWeight: '900',
    marginBottom: 6,
  },
  continueTitle: {
    color: colors.buttonText,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '900',
    marginBottom: 6,
  },
  continueMeta: {
    color: '#DDF3FF',
    fontSize: 12,
    fontWeight: '700',
  },
  playButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 14,
  },
  favoritePeopleSection: {
    marginBottom: 22,
  },
  subsectionTitle: {
    color: colors.textPrimary,
    fontSize: 15,
    fontWeight: '900',
    marginBottom: 12,
  },
  peopleRow: {
    paddingRight: 24,
    gap: 12,
  },
  personCard: {
    width: 86,
    alignItems: 'center',
  },
  personAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.card,
    marginBottom: 8,
  },
  personName: {
    color: colors.textPrimary,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '900',
    textAlign: 'center',
    width: 86,
  },
  savedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 24,
  },
  savedCard: {
    marginBottom: 4,
  },
  savedCover: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 18,
    backgroundColor: colors.card,
    marginBottom: 10,
  },
  savedTitle: {
    color: colors.textPrimary,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: '900',
    marginBottom: 4,
  },
  savedAuthor: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '700',
  },
  emptySaved: {
    alignItems: 'center',
    backgroundColor: colors.panel,
    borderRadius: 22,
    padding: 20,
  },
  emptyTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 6,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
});
